import React from 'react';
import { useNavigate } from 'react-router-dom';
import { IconType } from 'react-icons';
import { FaBell, FaBriefcase, FaEnvelope, FaExchangeAlt, FaInfoCircle, FaPauseCircle, FaSearch, FaUserCircle, FaUserSlash } from 'react-icons/fa';
import { NotificationEvent, NotificationType } from '../../types/notifications';
import { notificationTarget } from './notificationTarget';
import css from './NotificationPanel.module.scss';

interface NotificationItemProps {
  notification: NotificationEvent;
  /** Lets the host dismiss the panel once the row has navigated away. */
  onNavigate?: () => void;
}

const typeIcons: Record<NotificationType, IconType> = {
  search_status_changed: FaSearch,
  crawl_progress: FaSearch,
  opportunities_ready: FaBriefcase,
  application_status_changed: FaEnvelope,
  contact_enrichment_completed: FaUserCircle,
  retry_contact_ready: FaExchangeAlt,
  recommended_action_changed: FaBell,
  search_idle_warning: FaSearch,
  search_auto_paused: FaPauseCircle,
  // Same vocabulary as the other "different contact" events.
  contact_switched: FaExchangeAlt,
  contact_switch_proposed: FaExchangeAlt,
  contact_unreachable: FaUserSlash,
  // Refresh-only: never stored, so never rendered. Listed for the Record.
  contact_status_changed: FaUserCircle,
};

const typeLabels: Record<NotificationType, string> = {
  search_status_changed: 'Search update',
  crawl_progress: 'Crawl progress',
  opportunities_ready: 'Matches ready',
  application_status_changed: 'Application update',
  contact_enrichment_completed: 'Contact enriched',
  retry_contact_ready: 'Retry contact',
  recommended_action_changed: 'Action update',
  search_idle_warning: 'Search idle',
  search_auto_paused: 'Search paused',
  contact_switched: 'Contact switched',
  contact_switch_proposed: 'Contact suggested',
  contact_unreachable: 'No reachable contact',
  contact_status_changed: 'Contact update',
};

const typeIconClass: Record<NotificationType, string> = {
  search_status_changed: css.iconSearch,
  crawl_progress: css.iconSearch,
  opportunities_ready: css.iconOpportunity,
  application_status_changed: css.iconApplication,
  contact_enrichment_completed: css.iconContact,
  retry_contact_ready: css.iconContact,
  recommended_action_changed: css.iconApplication,
  search_idle_warning: css.iconSearch,
  search_auto_paused: css.iconSearch,
  contact_switched: css.iconContact,
  contact_switch_proposed: css.iconContact,
  // Grey, like the drawer's unreachable panel: nothing more is coming.
  contact_unreachable: css.iconSystem,
  contact_status_changed: css.iconContact,
};

const formatRelativeTime = (date: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  return `${diffDay}d ago`;
};

const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onNavigate }) => {
  const navigate = useNavigate();
  const Icon = typeIcons[notification.notificationType] || FaInfoCircle;
  const iconClass = typeIconClass[notification.notificationType] || css.iconSystem;
  const typeLabel = typeLabels[notification.notificationType] || 'Notification';

  // The daily sweep sends `metadata: { count }` and nothing else, so without
  // this the whole drop renders as a bare title once the row scrolls out of
  // memory. Explicit > 0 rather than a truthiness filter: a 0-count drop is
  // not a thing to advertise, but it shouldn't crash the chip row either.
  const count = notification.metadata?.count;
  const metadataChips = [
    typeof count === 'number' && count > 0 ? `${count} new` : undefined,
    notification.metadata?.role,
    notification.metadata?.location,
    notification.metadata?.status,
  ].filter(Boolean) as string[];

  const target = notificationTarget(notification);

  const body = (
    <>
      <div className={`${css.iconWrapper} ${iconClass}`}>
        <Icon />
      </div>
      <div className={css.content}>
        <div className={css.headerRow}>
          <div className={css.headingBlock}>
            <span className={css.typeBadge}>{typeLabel}</span>
            <span className={css.title}>{notification.title}</span>
          </div>
          <span className={css.timestamp}>{formatRelativeTime(notification.receivedAt)}</span>
        </div>
        {metadataChips.length > 0 && (
          <div className={css.metaRow}>
            {metadataChips.map((chip) => (
              <span key={chip} className={css.metaChip}>{chip}</span>
            ))}
          </div>
        )}
        <p className={css.message}>{notification.message}</p>
      </div>
      {!notification.read && <div className={css.unreadDot} />}
    </>
  );

  const className = `${css.notificationItem} ${!notification.read ? css.unread : ''}`;

  // Rows with nowhere to go stay exactly as inert as they have always been —
  // the panel grows an affordance only where clicking actually resolves.
  if (!target) {
    return <div className={className}>{body}</div>;
  }

  return (
    <button
      type="button"
      className={`${className} ${css.actionable}`}
      onClick={() => {
        navigate(target);
        onNavigate?.();
      }}
    >
      {body}
    </button>
  );
};

export default NotificationItem;
